import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";

import {
  getManualChecklistList,
  deleteManualChecklist,
  downloadManualChecklistReport,
  getManualTemplateList,
} from "../api/manualChecklistApi";

import { getAssignedProjects, getUsersWithRoles } from "../api/index";
import { deduplicateRoles } from "../utils/UserUtils";

// Current date helper formatted YYYY-MM-DD in Asia/Kolkata timezone
export const getTodayDateString = () => {
  try {
    const formatter = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Kolkata",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    return formatter.format(new Date()); // Formats as YYYY-MM-DD
  } catch (e) {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
};

// Current time helper formatted HH:mm in Asia/Kolkata timezone
export const getCurrentTimeString = () => {
  try {
    const formatter = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    return formatter.format(new Date()); // Formats as HH:mm
  } catch (e) {
    const d = new Date();
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  }
};

export const shiftDateString = (dateStr, days) => {
  if (!dateStr) return getTodayDateString();
  const [year, month, day] = dateStr.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() + days);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export const formatDateDisplay = (dateStr) => {
  if (!dateStr) return "";
  try {
    const [year, month, day] = dateStr.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch (e) {
    return dateStr;
  }
};

export const formatTimeDisplay = (timeStr) => {
  if (!timeStr) return "";
  try {
    const parts = String(timeStr).split(":");
    const hours = Number(parts[0]);
    const minutes = parts[1] || "00";
    if (isNaN(hours)) return timeStr;
    const period = hours >= 12 ? "PM" : "AM";
    const h12 = hours % 12 || 12;
    return `${h12}:${minutes} ${period}`;
  } catch (e) {
    return timeStr;
  }
};

// Date Range and Time matcher ensuring records match selected date range and time (minute precision) in Asia/Kolkata timezone
export const filterBySelectedDateRangeTime = (list, fromDate, toDate, targetTime) => {
  if (!Array.isArray(list)) return [];
  if (!fromDate && !toDate && !targetTime) return list;

  return list.filter((item) => {
    // 1. DATE RANGE CHECK
    let itemDate = null;
    if (item.checklist_date) {
      itemDate = String(item.checklist_date).trim().slice(0, 10);
    } else if (item.created_at) {
      try {
        itemDate = new Intl.DateTimeFormat("en-CA", {
          timeZone: "Asia/Kolkata",
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        }).format(new Date(item.created_at));
      } catch (e) {
        itemDate = String(item.created_at).slice(0, 10);
      }
    }

    if (fromDate || toDate) {
      if (!itemDate) return false;
      if (fromDate && itemDate < fromDate) return false;
      if (toDate && itemDate > toDate) return false;
    }

    // 2. TIME CHECK (if targetTime is provided)
    if (!targetTime) return true;

    const targetHHMM = String(targetTime).trim().slice(0, 5);

    if (item.checklist_time) {
      const itemHHMM = String(item.checklist_time).trim().slice(0, 5);
      return itemHHMM === targetHHMM;
    }

    // Legacy check: fallback to created_at in Asia/Kolkata
    if (item.created_at) {
      try {
        const createdTimeStr = new Intl.DateTimeFormat("en-GB", {
          timeZone: "Asia/Kolkata",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        }).format(new Date(item.created_at));
        return createdTimeStr === targetHHMM;
      } catch (e) {
        return false;
      }
    }

    return false;
  });
};

// Backward-compatible wrapper supporting (list, targetDate, targetTime) or (list, fromDate, toDate, targetTime)
export const filterBySelectedDateTime = (list, fromDateOrDate, toDateOrTime, maybeTime) => {
  if (typeof toDateOrTime === "string" && /^\d{4}-\d{2}-\d{2}$/.test(toDateOrTime)) {
    return filterBySelectedDateRangeTime(list, fromDateOrDate, toDateOrTime, maybeTime);
  }
  return filterBySelectedDateRangeTime(list, fromDateOrDate, fromDateOrDate, toDateOrTime);
};

export const filterBySelectedDate = (list, targetDate) =>
  filterBySelectedDateRangeTime(list, targetDate, targetDate, null);

export default function ManualLocationChecklistView({
  onOpenChecklist,
  initialDate,
  initialFromDate,
  initialToDate,
  initialTime,
}) {
  const navigate = useNavigate();

  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [projectRoles, setProjectRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");

  const defaultFrom = initialFromDate || initialDate || getTodayDateString();
  const defaultTo = initialToDate || initialDate || getTodayDateString();

  const [fromDate, setFromDate] = useState(defaultFrom);
  const [toDate, setToDate] = useState(defaultTo);
  const [dateRangeError, setDateRangeError] = useState(
    defaultFrom > defaultTo ? "From Date cannot be later than To Date." : ""
  );
  const [selectedTime, setSelectedTime] = useState(
    initialTime || getCurrentTimeString(),
  );

  const [createdChecklists, setCreatedChecklists] = useState([]);
  const [allChecklistsForCounts, setAllChecklistsForCounts] = useState([]);
  const [countsLoading, setCountsLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // Stale API response prevention
  const listReqIdRef = useRef(0);
  const countsReqIdRef = useRef(0);

  // Keep fromDate, toDate, and selectedTime in sync if initial props change
  useEffect(() => {
    if (initialFromDate) {
      setFromDate(initialFromDate);
    } else if (initialDate) {
      setFromDate(initialDate);
    }
  }, [initialFromDate, initialDate]);

  useEffect(() => {
    if (initialToDate) {
      setToDate(initialToDate);
    } else if (initialDate) {
      setToDate(initialDate);
    }
  }, [initialToDate, initialDate]);

  useEffect(() => {
    if (initialTime) {
      setSelectedTime(initialTime);
    }
  }, [initialTime]);

  // =====================================================
  // FETCH PROJECTS
  // =====================================================

  useEffect(() => {
    fetchAssignedProjects();
  }, []);

  // Fetch checklists whenever filters, fromDate, toDate, or selectedTime change
  useEffect(() => {
    if (selectedProject && selectedRole) {
      if (fromDate && toDate && fromDate > toDate) {
        setDateRangeError("From Date cannot be later than To Date.");
        setCreatedChecklists([]);
        return;
      }
      setDateRangeError("");
      fetchCreatedChecklists(fromDate, toDate, selectedTime);
    } else {
      setCreatedChecklists([]);
    }
  }, [
    selectedProject,
    selectedRole,
    selectedStatus,
    selectedTemplate,
    fromDate,
    toDate,
    selectedTime,
  ]);

  // Fetch unfiltered-by-status list for summary card counts whenever
  // project, role, fromDate, toDate, or selectedTime changes
  useEffect(() => {
    if (selectedProject && selectedRole) {
      if (fromDate && toDate && fromDate > toDate) {
        setAllChecklistsForCounts([]);
        return;
      }
      fetchAllChecklistsForCounts(fromDate, toDate, selectedTime);
    } else {
      setAllChecklistsForCounts([]);
    }
  }, [selectedProject, selectedRole, fromDate, toDate, selectedTime]);

  const fetchAssignedProjects = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem("USER_DATA"));
      const userId = userData?.id;
      const res = await getAssignedProjects(userId);
      setProjects(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  // =====================================================
  // FETCH MY ROLES IN PROJECT
  // =====================================================

  const fetchMyProjectRoles = async (projectId) => {
    try {
      const res = await getUsersWithRoles(projectId);
      const userData = JSON.parse(localStorage.getItem("USER_DATA"));

      const currentUser = (res.data || []).find(
        (user) => String(user.user_id) === String(userData?.id),
      );

      let roles = [];
      if (currentUser && Array.isArray(currentUser.roles)) {
        roles = [...currentUser.roles];
      }

      // Fallback: Check userData.accesses or localStorage ACCESSES or ROLE for this project
      const storedAccesses =
        userData?.accesses ||
        JSON.parse(localStorage.getItem("ACCESSES") || "[]");
      const projectAccesses = (
        Array.isArray(storedAccesses) ? storedAccesses : []
      ).filter(
        (acc) =>
          String(acc.project_id || acc.projectId || acc.project) ===
          String(projectId),
      );

      projectAccesses.forEach((acc) => {
        if (Array.isArray(acc.roles)) {
          acc.roles.forEach((r) => {
            if (r && !roles.includes(r)) roles.push(r);
          });
        } else if (acc.role && !roles.includes(acc.role)) {
          roles.push(acc.role);
        } else if (acc.role_name && !roles.includes(acc.role_name)) {
          roles.push(acc.role_name);
        }
      });

      const storedRole = localStorage.getItem("ROLE") || userData?.role;
      if (storedRole && !roles.includes(storedRole)) {
        roles.push(storedRole);
      }

      const dedupedRoles = deduplicateRoles(roles);
      setProjectRoles(dedupedRoles);

      setSelectedRole((prevRole) => {
        if (!prevRole) return prevRole;
        const matched = dedupedRoles.find(
          (r) =>
            r.toLowerCase().replace(/[-_]+/g, " ") ===
            prevRole.toLowerCase().replace(/[-_]+/g, " "),
        );
        return matched || prevRole;
      });
    } catch (err) {
      console.error(err);
    }
  };

  // =====================================================
  // FETCH CHECKLISTS
  // =====================================================

  const fetchCreatedChecklists = async (
    fromDateToUse = fromDate,
    toDateToUse = toDate,
    timeToUse = selectedTime,
  ) => {
    if (!selectedProject || !selectedRole) {
      setCreatedChecklists([]);
      return;
    }
    if (fromDateToUse && toDateToUse && fromDateToUse > toDateToUse) {
      setCreatedChecklists([]);
      return;
    }

    const reqId = ++listReqIdRef.current;
    try {
      setLoading(true);
      const filters = {
        project_id: selectedProject,
        status: selectedStatus,
        template_id: selectedTemplate,
      };
      if (fromDateToUse) filters.from_date = fromDateToUse;
      if (toDateToUse) filters.to_date = toDateToUse;
      if (fromDateToUse && toDateToUse && fromDateToUse === toDateToUse) {
        filters.date = fromDateToUse;
      }
      if (timeToUse) filters.time = timeToUse;

      const res = await getManualChecklistList(selectedRole, filters);

      if (reqId === listReqIdRef.current) {
        const rawList = res.data?.results || res.data || [];
        setCreatedChecklists(
          filterBySelectedDateRangeTime(rawList, fromDateToUse, toDateToUse, timeToUse),
        );
      }
    } catch (err) {
      if (reqId === listReqIdRef.current) {
        console.error(err);
        setCreatedChecklists([]);
      }
    } finally {
      if (reqId === listReqIdRef.current) {
        setLoading(false);
      }
    }
  };

  // =====================================================
  // FETCH UNFILTERED LIST (FOR SUMMARY CARD COUNTS)
  // =====================================================

  const fetchAllChecklistsForCounts = async (
    fromDateToUse = fromDate,
    toDateToUse = toDate,
    timeToUse = selectedTime,
  ) => {
    if (!selectedProject || !selectedRole) {
      setAllChecklistsForCounts([]);
      return;
    }
    if (fromDateToUse && toDateToUse && fromDateToUse > toDateToUse) {
      setAllChecklistsForCounts([]);
      return;
    }

    const reqId = ++countsReqIdRef.current;
    try {
      setCountsLoading(true);
      const filters = {
        project_id: selectedProject,
      };
      if (fromDateToUse) filters.from_date = fromDateToUse;
      if (toDateToUse) filters.to_date = toDateToUse;
      if (fromDateToUse && toDateToUse && fromDateToUse === toDateToUse) {
        filters.date = fromDateToUse;
      }
      if (timeToUse) filters.time = timeToUse;

      const res = await getManualChecklistList(selectedRole, filters);

      if (reqId === countsReqIdRef.current) {
        const rawList = res.data?.results || res.data || [];
        setAllChecklistsForCounts(
          filterBySelectedDateRangeTime(rawList, fromDateToUse, toDateToUse, timeToUse),
        );
      }
    } catch (err) {
      if (reqId === countsReqIdRef.current) {
        console.error(err);
        setAllChecklistsForCounts([]);
      }
    } finally {
      if (reqId === countsReqIdRef.current) {
        setCountsLoading(false);
      }
    }
  };

  const fetchTemplates = async (projectId) => {
    try {
      const res = await getManualTemplateList(Number(projectId));
      setTemplates(res.data?.results || res.data || []);
    } catch (err) {
      console.error(err);
      setTemplates([]);
    }
  };

  const handleDeleteChecklist = async (checklistId) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this checklist?",
    );

    if (!confirmed) return;

    try {
      await deleteManualChecklist(checklistId);
      fetchCreatedChecklists(fromDate, toDate, selectedTime);
      fetchAllChecklistsForCounts(fromDate, toDate, selectedTime);
    } catch (err) {
      console.error(err);
      alert("Failed to delete checklist.");
    }
  };

  const handleDownloadReport = async (checklistId) => {
    try {
      const response = await downloadManualChecklistReport(
        checklistId,
        selectedRole,
      );

      const blob = new Blob([response.data], {
        type: "application/pdf",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `manual-checklist-${checklistId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert("Failed to download report");
    }
  };

  // =====================================================
  // DATE RANGE & TIME HANDLERS
  // =====================================================

  const handleFromDateChange = (newFromDate) => {
    setFromDate(newFromDate);
    setCurrentPage(1);
    if (newFromDate && toDate && newFromDate > toDate) {
      setDateRangeError("From Date cannot be later than To Date.");
    } else {
      setDateRangeError("");
    }
  };

  const handleToDateChange = (newToDate) => {
    setToDate(newToDate);
    setCurrentPage(1);
    if (fromDate && newToDate && fromDate > newToDate) {
      setDateRangeError("From Date cannot be later than To Date.");
    } else {
      setDateRangeError("");
    }
  };

  const handleTimeChange = (newTime) => {
    setSelectedTime(newTime);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSelectedStatus("");
    setSelectedTemplate("");
    const today = getTodayDateString();
    setFromDate(today);
    setToDate(today);
    setDateRangeError("");
    setSelectedTime(getCurrentTimeString());
    setCurrentPage(1);
  };

  // =====================================================
  // STATUS SUMMARY COUNTS
  // =====================================================

  const normalizeStatus = (status) =>
    String(status || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_");

  const isPendingStatus = (status) => {
    const s = normalizeStatus(status);
    return s === "pending" || s === "open";
  };

  const isInProgressStatus = (status) => {
    const s = normalizeStatus(status);
    return s === "in_progress" || s === "inprogress";
  };

  const isCompletedStatus = (status) => {
    const s = normalizeStatus(status);
    return s === "completed" || s === "complete" || s === "done";
  };

  const totalCount = allChecklistsForCounts.length;

  const completedCount = allChecklistsForCounts.filter((item) =>
    isCompletedStatus(item.status),
  ).length;

  const inProgressCount = allChecklistsForCounts.filter((item) =>
    isInProgressStatus(item.status),
  ).length;

  const pendingCount = allChecklistsForCounts.filter((item) =>
    isPendingStatus(item.status),
  ).length;

  const statusCards = [
    {
      key: "",
      label: "All",
      count: totalCount,
      accent: "border-[#d8c2a5] bg-[#f3e2c9] text-[#6b533d]",
      activeAccent: "border-[#4b3b2a] bg-[#4b3b2a] text-white",
    },
    {
      key: "completed",
      label: "Completed",
      count: completedCount,
      accent: "border-green-200 bg-green-50 text-green-700",
      activeAccent: "border-green-600 bg-green-600 text-white",
    },
    {
      key: "in_progress",
      label: "In Progress",
      count: inProgressCount,
      accent: "border-amber-200 bg-amber-50 text-amber-700",
      activeAccent: "border-amber-600 bg-amber-600 text-white",
    },
    {
      key: "pending",
      label: "Pending",
      count: pendingCount,
      accent: "border-red-200 bg-red-50 text-red-700",
      activeAccent: "border-red-600 bg-red-600 text-white",
    },
  ];

  const handleStatusCardClick = (cardKey) => {
    setCurrentPage(1);
    if (cardKey === "pending") {
      setSelectedStatus("open");
    } else {
      setSelectedStatus(cardKey);
    }
  };

  const isCardActive = (cardKey) => {
    if (cardKey === "") return selectedStatus === "";
    if (cardKey === "pending") return selectedStatus === "open";
    return selectedStatus === cardKey;
  };

  // =====================================================
  // PAGINATION
  // =====================================================

  const totalPages = Math.ceil(createdChecklists.length / pageSize) || 1;
  const paginatedChecklists = createdChecklists.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  return (
    <div className="space-y-6 mt-5">
      {/* ===================================================== */}
      {/* HEADER */}
      {/* ===================================================== */}

      <div className="rounded-3xl border border-[#eadcc8] bg-[#f7efe3] p-6">
        <div>
          <h1 className="text-2xl font-semibold text-[#4b3b2a]">
            My Assigned Checklists
          </h1>

          <p className="mt-2 text-sm text-[#8b7765]">
            View and manage checklist workflows.
          </p>
        </div>
      </div>

      {/* ===================================================== */}
      {/* STATUS SUMMARY CARDS */}
      {/* ===================================================== */}

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {statusCards.map((card) => {
          const active = isCardActive(card.key);

          return (
            <button
              key={card.label}
              type="button"
              onClick={() => handleStatusCardClick(card.key)}
              className={`rounded-2xl border p-5 text-left transition ${
                active ? card.activeAccent : card.accent
              }`}
            >
              <p
                className={`text-xs font-medium uppercase tracking-wide ${
                  active ? "text-white/80" : "opacity-70"
                }`}
              >
                {card.label}
              </p>

              <p className="mt-2 text-3xl font-semibold">
                {countsLoading ? "…" : card.count}
              </p>
            </button>
          );
        })}
      </div>

      {/* ===================================================== */}
      {/* FILTERS */}
      {/* ===================================================== */}

      <div className="rounded-3xl border border-[#eadcc8] bg-[#f7efe3] p-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8 items-end">
          {/* PROJECT */}
          <div>
            <label className="mb-2 block text-sm font-medium text-[#6b533d]">
              Project
            </label>

            <select
              value={selectedProject}
              onChange={(e) => {
                const projectId = e.target.value;
                setSelectedProject(projectId);
                setSelectedRole("");
                setSelectedStatus("");
                setSelectedTemplate("");
                setCurrentPage(1);
                fetchMyProjectRoles(projectId);
                fetchTemplates(projectId);
              }}
              className="w-full rounded-xl border border-[#e1d1bd] bg-[#fffaf4] px-4 py-3 text-sm text-[#4b3b2a] outline-none"
            >
              <option value="">Select project</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>

          {/* ROLE */}
          <div>
            <label className="mb-2 block text-sm font-medium text-[#6b533d]">
              Role
            </label>

            <select
              value={selectedRole}
              onChange={(e) => {
                setSelectedRole(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full rounded-xl border border-[#e1d1bd] bg-[#fffaf4] px-4 py-3 text-sm text-[#4b3b2a] outline-none"
            >
              <option value="">Select role</option>
              {projectRoles.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>
          </div>

          {/* TEMPLATE */}
          <div>
            <label className="mb-2 block text-sm font-medium text-[#6b533d]">
              Template
            </label>

            <select
              value={selectedTemplate}
              onChange={(e) => {
                setSelectedTemplate(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full rounded-xl border border-[#e1d1bd] bg-[#fffaf4] px-4 py-3 text-sm text-[#4b3b2a] outline-none"
            >
              <option value="">All Templates</option>
              {templates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
            </select>
          </div>

          {/* STATUS */}
          <div>
            <label className="mb-2 block text-sm font-medium text-[#6b533d]">
              Status
            </label>

            <select
              value={selectedStatus}
              onChange={(e) => {
                setSelectedStatus(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full rounded-xl border border-[#e1d1bd] bg-[#fffaf4] px-4 py-3 text-sm text-[#4b3b2a] outline-none"
            >
              <option value="">All Statuses</option>
              <option value="open">Open (Pending)</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          {/* FROM DATE */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <label htmlFor="from-date-filter" className="block text-sm font-medium text-[#6b533d]">
                From Date
              </label>
              {fromDate === getTodayDateString() ? (
                <span className="rounded-md bg-[#e7d8c4] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#6b533d]">
                  Today
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => handleFromDateChange(getTodayDateString())}
                  className="text-xs font-semibold text-[#8b5e34] underline hover:text-[#5c3e21]"
                >
                  Today
                </button>
              )}
            </div>

            <div className="flex items-center gap-1">
              <button
                type="button"
                title="Previous Day"
                onClick={() => handleFromDateChange(shiftDateString(fromDate, -1))}
                className="flex h-[46px] w-9 items-center justify-center rounded-xl border border-[#e1d1bd] bg-[#fffaf4] text-base font-bold text-[#6b533d] transition hover:bg-[#f3e2c9]"
              >
                ‹
              </button>

              <input
                id="from-date-filter"
                type="date"
                value={fromDate}
                onChange={(e) => handleFromDateChange(e.target.value)}
                className={`h-[46px] min-w-0 flex-1 rounded-xl border ${
                  dateRangeError ? "border-red-400 bg-red-50" : "border-[#e1d1bd] bg-[#fffaf4]"
                } px-2.5 py-2 text-sm text-[#4b3b2a] outline-none`}
              />

              <button
                type="button"
                title="Next Day"
                onClick={() => handleFromDateChange(shiftDateString(fromDate, 1))}
                className="flex h-[46px] w-9 items-center justify-center rounded-xl border border-[#e1d1bd] bg-[#fffaf4] text-base font-bold text-[#6b533d] transition hover:bg-[#f3e2c9]"
              >
                ›
              </button>
            </div>
          </div>

          {/* TO DATE */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <label htmlFor="to-date-filter" className="block text-sm font-medium text-[#6b533d]">
                To Date
              </label>
              {toDate === getTodayDateString() ? (
                <span className="rounded-md bg-[#e7d8c4] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#6b533d]">
                  Today
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => handleToDateChange(getTodayDateString())}
                  className="text-xs font-semibold text-[#8b5e34] underline hover:text-[#5c3e21]"
                >
                  Today
                </button>
              )}
            </div>

            <div className="flex items-center gap-1">
              <button
                type="button"
                title="Previous Day"
                onClick={() => handleToDateChange(shiftDateString(toDate, -1))}
                className="flex h-[46px] w-9 items-center justify-center rounded-xl border border-[#e1d1bd] bg-[#fffaf4] text-base font-bold text-[#6b533d] transition hover:bg-[#f3e2c9]"
              >
                ‹
              </button>

              <input
                id="to-date-filter"
                type="date"
                value={toDate}
                onChange={(e) => handleToDateChange(e.target.value)}
                className={`h-[46px] min-w-0 flex-1 rounded-xl border ${
                  dateRangeError ? "border-red-400 bg-red-50" : "border-[#e1d1bd] bg-[#fffaf4]"
                } px-2.5 py-2 text-sm text-[#4b3b2a] outline-none`}
              />

              <button
                type="button"
                title="Next Day"
                onClick={() => handleToDateChange(shiftDateString(toDate, 1))}
                className="flex h-[46px] w-9 items-center justify-center rounded-xl border border-[#e1d1bd] bg-[#fffaf4] text-base font-bold text-[#6b533d] transition hover:bg-[#f3e2c9]"
              >
                ›
              </button>
            </div>
          </div>

          {/* TIME */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <label htmlFor="time-filter" className="block text-sm font-medium text-[#6b533d]">
                Time
              </label>
              <div className="flex items-center gap-1.5">
                {selectedTime ? (
                  <button
                    type="button"
                    onClick={() => handleTimeChange("")}
                    className="text-[11px] font-semibold text-[#8b5e34] underline hover:text-[#5c3e21]"
                    title="Show all times for this date range"
                  >
                    All Day
                  </button>
                ) : null}
                {selectedTime === getCurrentTimeString() ? (
                  <span className="rounded-md bg-[#e7d8c4] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#6b533d]">
                    Now
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleTimeChange(getCurrentTimeString())}
                    className="text-xs font-semibold text-[#8b5e34] underline hover:text-[#5c3e21]"
                  >
                    Now
                  </button>
                )}
              </div>
            </div>

            <div className="flex items-center gap-1">
              <input
                id="time-filter"
                type="time"
                value={selectedTime}
                onChange={(e) => handleTimeChange(e.target.value)}
                className="h-[46px] min-w-0 flex-1 rounded-xl border border-[#e1d1bd] bg-[#fffaf4] px-3 py-2 text-sm text-[#4b3b2a] outline-none"
              />
            </div>
          </div>

          {/* CLEAR FILTERS */}
          <div>
            <button
              type="button"
              onClick={handleClearFilters}
              className="h-[46px] w-full rounded-xl border border-[#d8c2a5] bg-[#f3e2c9] px-4 text-sm font-medium text-[#6b533d] transition hover:bg-[#ecd5b4]"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {dateRangeError && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-xs font-medium text-red-700">
            <span className="flex items-center gap-1.5">
              <span>⚠️</span>
              <span>{dateRangeError}</span>
            </span>
            <button
              type="button"
              onClick={() => {
                setToDate(fromDate);
                setDateRangeError("");
              }}
              className="font-semibold underline hover:text-red-900"
            >
              Align To Date with From Date
            </button>
          </div>
        )}
      </div>

      {/* ===================================================== */}
      {/* CHECKLISTS */}
      {/* ===================================================== */}

      <div className="rounded-3xl border border-[#eadcc8] bg-[#f7efe3] p-6">
        <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-[#4b3b2a]">
              Manual Checklists
            </h2>

            <p className="mt-1 text-sm text-[#8b7765]">
              {fromDate === toDate ? (
                <>
                  Showing records for{" "}
                  <span className="font-semibold text-[#4b3b2a]">
                    {formatDateDisplay(fromDate)}
                  </span>
                </>
              ) : (
                <>
                  Showing records from{" "}
                  <span className="font-semibold text-[#4b3b2a]">
                    {formatDateDisplay(fromDate)}
                  </span>{" "}
                  to{" "}
                  <span className="font-semibold text-[#4b3b2a]">
                    {formatDateDisplay(toDate)}
                  </span>
                </>
              )}
              {selectedTime ? (
                <>
                  {" "}at{" "}
                  <span className="font-semibold text-[#4b3b2a]">
                    {formatTimeDisplay(selectedTime)}
                  </span>
                </>
              ) : (
                " (All Day)"
              )}
              {fromDate === getTodayDateString() && toDate === getTodayDateString() ? " • Today" : ""}
              {createdChecklists.length > 0 &&
                ` • ${createdChecklists.length} ${
                  createdChecklists.length === 1 ? "record" : "records"
                } found`}
            </p>
          </div>

          {/* Current date & time indicator badge */}
          <div className="flex flex-wrap items-center gap-2 rounded-xl border border-[#e1d1bd] bg-[#fffaf4] px-3 py-1.5 text-xs font-medium text-[#6b533d]">
            <span>
              📅 {fromDate === toDate ? fromDate : `${fromDate} → ${toDate}`}
            </span>
            <span>•</span>
            <span>⏰ {selectedTime ? formatTimeDisplay(selectedTime) : "All Day"}</span>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center p-10 text-sm text-[#8b7765]">
            <div className="animate-pulse">
              Loading QC Forms for{" "}
              {fromDate === toDate
                ? formatDateDisplay(fromDate)
                : `${formatDateDisplay(fromDate)} to ${formatDateDisplay(toDate)}`}
              {selectedTime ? ` at ${formatTimeDisplay(selectedTime)}` : ""}...
            </div>
          </div>
        ) : dateRangeError ? (
          <div className="rounded-2xl border border-dashed border-red-300 bg-red-50/50 p-10 text-center">
            <div className="mx-auto mb-2 text-3xl">⚠️</div>
            <h3 className="text-base font-semibold text-red-600">
              Invalid Date Range
            </h3>
            <p className="mt-1 text-sm text-[#8b7765]">
              {dateRangeError} Please select an end date that is on or after the start date.
            </p>
            <div className="mt-4 flex items-center justify-center gap-2">
              <button
                type="button"
                onClick={() => {
                  setToDate(fromDate);
                  setDateRangeError("");
                }}
                className="rounded-xl border border-[#d8c2a5] bg-[#f3e2c9] px-4 py-2 text-xs font-semibold text-[#6b533d] transition hover:bg-[#ecd5b4]"
              >
                Set To Date to {formatDateDisplay(fromDate)}
              </button>
            </div>
          </div>
        ) : createdChecklists.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-[#d8c2a5] bg-[#fffaf4] p-10 text-center">
            <div className="mx-auto mb-2 text-3xl">📋</div>
            <h3 className="text-base font-semibold text-[#4b3b2a]">
              No checklists found
            </h3>
            <p className="mt-1 text-sm text-[#8b7765]">
              No checklist records match{" "}
              {fromDate === toDate
                ? formatDateDisplay(fromDate)
                : `${formatDateDisplay(fromDate)} to ${formatDateDisplay(toDate)}`}
              {selectedTime ? ` at ${formatTimeDisplay(selectedTime)}` : ""} with the selected filters.
            </p>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
              {selectedTime && (
                <button
                  type="button"
                  onClick={() => handleTimeChange("")}
                  className="rounded-xl border border-[#d8c2a5] bg-[#f3e2c9] px-4 py-2 text-xs font-semibold text-[#6b533d] transition hover:bg-[#ecd5b4]"
                >
                  View All Day
                </button>
              )}
              {(fromDate !== getTodayDateString() || toDate !== getTodayDateString()) && (
                <button
                  type="button"
                  onClick={() => {
                    const today = getTodayDateString();
                    setFromDate(today);
                    setToDate(today);
                    setDateRangeError("");
                  }}
                  className="rounded-xl border border-[#d8c2a5] bg-[#fffaf4] px-4 py-2 text-xs font-semibold text-[#6b533d] transition hover:bg-[#f3e2c9]"
                >
                  Go to Today ({formatDateDisplay(getTodayDateString())})
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {paginatedChecklists.map((item) => (
              <div
                key={item.id}
                className="rounded-2xl border border-[#eadcc8] bg-[#fffaf4] p-5 shadow-sm transition hover:shadow"
              >
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div className="space-y-2">
                    <h3 className="text-lg font-semibold text-[#4b3b2a]">
                      #{item.id} {item.template_name}
                    </h3>

                    <div className="flex flex-wrap items-center gap-2 text-sm text-[#8b7765]">
                      <span>
                        Project: {item.project_name || item.project_id}
                      </span>

                      <span>•</span>

                      <span>Location: {item.location_text}</span>

                      <span>•</span>

                      <span>
                        Status:{" "}
                        <span className="font-medium capitalize text-[#4b3b2a]">
                          {item.status?.replace("_", " ")}
                        </span>
                      </span>

                      <span>•</span>

                      <span>
                        Date:{" "}
                        <span className="font-semibold text-[#4b3b2a]">
                          {item.checklist_date
                            ? formatDateDisplay(item.checklist_date)
                            : item.created_at
                            ? new Date(item.created_at).toLocaleDateString("en-IN", {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                              })
                            : "N/A"}
                        </span>
                      </span>

                      {(item.checklist_time || item.created_at) && (
                        <>
                          <span>•</span>
                          <span>
                            Time:{" "}
                            <span className="font-semibold text-[#4b3b2a]">
                              {item.checklist_time
                                ? formatTimeDisplay(item.checklist_time)
                                : item.created_at
                                ? new Date(item.created_at).toLocaleTimeString([], {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })
                                : "N/A"}
                            </span>
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() =>
                        onOpenChecklist({
                          checklistId: item.id,
                          role: selectedRole,
                        })
                      }
                      className="rounded-xl border border-[#d8c2a5] bg-[#f3e2c9] px-5 py-2 text-sm font-medium text-[#6b533d] transition hover:bg-[#ecd5b4]"
                    >
                      Open Checklist
                    </button>

                    <button
                      onClick={() => handleDownloadReport(item.id)}
                      className="rounded-xl border border-green-200 bg-green-100 px-5 py-2 text-sm font-medium text-green-700 transition hover:bg-green-200"
                    >
                      Download Report
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="mt-6 flex flex-col items-center justify-between gap-4 border-t border-[#eadcc8] pt-4 sm:flex-row">
                <p className="text-xs text-[#8b7765]">
                  Showing {(currentPage - 1) * pageSize + 1} to{" "}
                  {Math.min(currentPage * pageSize, createdChecklists.length)} of{" "}
                  {createdChecklists.length} records
                </p>

                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                    className="rounded-lg border border-[#e1d1bd] bg-[#fffaf4] px-3 py-1.5 text-xs font-medium text-[#6b533d] transition disabled:opacity-40 hover:bg-[#f3e2c9]"
                  >
                    Previous
                  </button>

                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                    .map((pageNum, idx, arr) => (
                      <React.Fragment key={pageNum}>
                        {idx > 0 && arr[idx - 1] !== pageNum - 1 && (
                          <span className="px-1 text-xs text-[#8b7765]">…</span>
                        )}
                        <button
                          type="button"
                          onClick={() => setCurrentPage(pageNum)}
                          className={`min-w-8 rounded-lg px-2.5 py-1.5 text-xs font-medium transition ${
                            currentPage === pageNum
                              ? "bg-[#4b3b2a] text-white"
                              : "border border-[#e1d1bd] bg-[#fffaf4] text-[#6b533d] hover:bg-[#f3e2c9]"
                          }`}
                        >
                          {pageNum}
                        </button>
                      </React.Fragment>
                    ))}

                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="rounded-lg border border-[#e1d1bd] bg-[#fffaf4] px-3 py-1.5 text-xs font-medium text-[#6b533d] transition disabled:opacity-40 hover:bg-[#f3e2c9]"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}